import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, ApiError, AccountPermissions, type SignupRole } from '../api/client';
import { FULL_PERMISSIONS, hasFeatureAccess, FeatureName } from '../permissions';
import i18n, {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  isSupportedLanguage,
  type SupportedLanguage,
} from '../i18n';
import { clearListCaches } from '../chats/listCache';
import { forgetCrmAccess } from '../crm/crmAccess';
import { applyStyling, createStylingUploader, isStylingKey, stylingPatchFrom, type StylingPatch } from '../theme/userStyling';
import type { SidebarLayout } from '../components/sidebar/sidebarLayout';

interface User {
  id: string;
  email: string;
  role: string;
  username?: string;
  language?: SupportedLanguage;
  // Settings → Styling saved on the profile; null/undefined => never saved.
  styling?: Record<string, unknown> | null;
  // Public URL of the profile picture; null/undefined => the initial is shown.
  avatar_url?: string | null;
  can_access_payments?: boolean;
  // null/undefined => new-user onboarding still owed; set => finished or skipped.
  onboarding_completed_at?: string | null;
  // true => the User owes a Telegram connection; everything else waits until it is made.
  telegram_required?: boolean;
  // Chosen at signup; null/undefined => created before signup asked (onboarding asks instead).
  signup_role?: SignupRole | null;
  // Ordered account_id list for the old flat left-sidebar arrangement. Superseded by
  // sidebar_layout; only read to seed it for a User who has never arranged groups.
  sidebar_account_order?: string[];
  // The User's Account groups on the Accounts list; null/absent => never arranged.
  sidebar_layout?: SidebarLayout | null;
}

/**
 * Apply a language to the running app: switch i18next and mirror the choice to
 * localStorage so it survives reloads and there is no flash before auth loads.
 */
function applyLanguage(language: string | undefined) {
  const lang: SupportedLanguage = isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE;
  if (i18n.language !== lang) {
    i18n.changeLanguage(lang);
  }
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // localStorage may be unavailable; ignore.
  }
}

/** Sends the browser's whole Settings → Styling to the profile, debounced. */
const PERMISSION_LOAD_ATTEMPTS = 3;
const PERMISSION_RETRY_DELAY_MS = 500;

// Opening the console while the server can't be reached retries the session check
// (2s, 5s, 10s, then every 30s) instead of logging the user out.
const AUTH_RETRY_DELAYS_MS = [2_000, 5_000, 10_000];
const AUTH_RETRY_EVERY_MS = 30_000;

/** The session check failed for a reason that says nothing about the session: the
 *  network or server is down (5xx, 429, no answer), or a 401 whose refresh could not
 *  reach the server (the refresh token is still here — a real rejection removes it). */
function isTransientAuthError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  if (error.status >= 500 || error.status === 429) return true;
  return error.status === 401 && api.hasSession();
}

const stylingUploader = createStylingUploader((styling) => api.updateMyProfile({ styling }));

/**
 * Bring the browser in line with the styling saved on the profile. A profile
 * that has none yet takes what this browser already uses, so choices made
 * before styling was saved per user are kept.
 */
function syncStyling(saved: Record<string, unknown> | null | undefined) {
  if (saved && typeof saved === 'object') applyStyling(stylingPatchFrom(saved));
  else void stylingUploader.schedule().catch(() => undefined);
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  /** The first session check could not reach the server and is retrying. */
  reconnecting: boolean;
  isSuperAdmin: boolean;
  isAuthenticated: boolean;
  accountPermissions: Record<string, AccountPermissions>;
  getPermissions: (accountId: string) => AccountPermissions | undefined;
  loadAccountPermissions: (accountId: string) => Promise<void>;
  hasAccess: (accountId: string, feature: FeatureName) => boolean;
  login: (identifier: string, password: string) => Promise<void>;
  signup: (email: string, password: string, username: string | undefined, signupRole: SignupRole) => Promise<void>;
  logout: () => Promise<void>;
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => Promise<void>;
  /**
   * Apply Settings → Styling changes at once and save them to the profile.
   * Resolves false when the browser could not store them; rejects when the
   * profile save failed.
   */
  saveStyling: (patch: StylingPatch) => Promise<boolean>;
  /** Show a just-saved (or removed, with null) profile picture everywhere at once. */
  setAvatarUrl: (url: string | null) => void;
  needsOnboarding: boolean;
  completeOnboarding: () => Promise<void>;
  needsTelegram: boolean;
  /** Release the Telegram gate once the bot has confirmed the connection. */
  markTelegramConnected: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const [accountPermissions, setAccountPermissions] = useState<Record<string, AccountPermissions>>({});

  useEffect(() => {
    let cancelled = false;
    const checkAuth = async () => {
      for (let attempt = 0; api.getToken() && !cancelled; attempt++) {
        try {
          const userData = await api.getCurrentUser();
          if (cancelled) return;
          setUser(userData as User);
          applyLanguage(userData.language);
          syncStyling(userData.styling);
          break;
        } catch (error) {
          if (cancelled) return;
          if (!isTransientAuthError(error)) {
            console.error('Auth check failed:', error);
            api.setToken(null);
            break;
          }
          // The server can't be reached right now: keep the session and try again
          // rather than sending the user to the login page.
          setReconnecting(true);
          await new Promise(resolve => setTimeout(resolve, AUTH_RETRY_DELAYS_MS[attempt] ?? AUTH_RETRY_EVERY_MS));
        }
      }
      if (cancelled) return;
      setReconnecting(false);
      setLoading(false);
    };

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const isSuperAdmin = user?.role === 'super_admin';
  const rawLanguage = user?.language;
  const language: SupportedLanguage = isSupportedLanguage(rawLanguage) ? rawLanguage : DEFAULT_LANGUAGE;

  const loadAccountPermissions = async (accountId: string) => {
    if (!user) return;

    if (isSuperAdmin) {
      setAccountPermissions(prev => ({ ...prev, [accountId]: FULL_PERMISSIONS }));
      return;
    }

    // A busy backend answers 503 now and then; without permissions every page in the
    // account reads as "no access" until reload, so retry a stalled load a few times.
    for (let attempt = 0; attempt < PERMISSION_LOAD_ATTEMPTS; attempt++) {
      try {
        const permissions = await api.getAccountPermissions(accountId);
        setAccountPermissions(prev => ({ ...prev, [accountId]: permissions }));
        return;
      } catch (error) {
        if (attempt + 1 === PERMISSION_LOAD_ATTEMPTS) {
          console.error(`Failed to load permissions for account ${accountId}:`, error);
          return;
        }
        await new Promise(resolve => setTimeout(resolve, PERMISSION_RETRY_DELAY_MS * (attempt + 1)));
      }
    }
  };

  const getPermissions = (accountId: string): AccountPermissions | undefined => {
    if (isSuperAdmin) return FULL_PERMISSIONS;
    return accountPermissions[accountId];
  };

  const hasAccess = (accountId: string, feature: FeatureName): boolean => {
    if (!user) return false;
    if (isSuperAdmin) return true;
    return hasFeatureAccess(accountPermissions[accountId], feature);
  };

  const login = async (identifier: string, password: string) => {
    const response = await api.login(identifier, password);
    setUser(response.user as User);
    setAccountPermissions({});
    applyLanguage(response.user.language);
    syncStyling((response.user as User).styling);
  };

  const signup = async (email: string, password: string, username: string | undefined, signupRole: SignupRole) => {
    const response = await api.signup(email, password, username, signupRole);
    if (response.user) {
      setUser(response.user as User);
      setAccountPermissions({});
      applyLanguage(response.user.language);
    }
  };

  const setLanguage = async (language: SupportedLanguage) => {
    // Apply immediately for a responsive UI, then persist to the backend.
    applyLanguage(language);
    setUser((prev) => (prev ? { ...prev, language } : prev));
    await api.updateMyProfile({ language });
  };

  const saveStyling = async (patch: StylingPatch) => {
    const stored = applyStyling(patch);
    await stylingUploader.schedule();
    return stored;
  };

  const setAvatarUrl = (url: string | null) => setUser((prev) => (prev ? { ...prev, avatar_url: url } : prev));

  // Styling changed in the CRM frame or another tab: save it to the profile too.
  const signedIn = !!user;
  useEffect(() => {
    if (!signedIn) return;
    const onStorage = (event: StorageEvent) => {
      if (isStylingKey(event.key)) void stylingUploader.schedule().catch(() => undefined);
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      stylingUploader.cancel();
    };
  }, [signedIn]);

  // Onboarding is owed only for an authenticated user with no completion stamp.
  const needsOnboarding = !!user && !user.onboarding_completed_at;

  // The Telegram connection is checked server-side; the gate only mirrors it.
  const needsTelegram = !!user?.telegram_required;
  const markTelegramConnected = () => setUser((prev) => (prev ? { ...prev, telegram_required: false } : prev));

  const completeOnboarding = async () => {
    if (!user) return;
    // Optimistically release the gate, then persist; ignore transient failures so a
    // user is never trapped in onboarding by a flaky write.
    setUser((prev) => (prev ? { ...prev, onboarding_completed_at: new Date().toISOString() } : prev));
    try {
      await api.completeOnboarding();
    } catch (error) {
      console.error('Failed to persist onboarding completion:', error);
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      // What the Chats page and the CRM gate remembered for this login stays behind.
      clearListCaches();
      forgetCrmAccess();
      setUser(null);
      setAccountPermissions({});
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    reconnecting,
    isSuperAdmin,
    isAuthenticated: !!user,
    accountPermissions,
    getPermissions,
    loadAccountPermissions,
    hasAccess,
    login,
    signup,
    logout,
    language,
    setLanguage,
    saveStyling,
    setAvatarUrl,
    needsOnboarding,
    completeOnboarding,
    needsTelegram,
    markTelegramConnected,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** The auth state, or undefined outside an AuthProvider (a component rendered on its
 *  own, as in tests). */
// eslint-disable-next-line react-refresh/only-export-components -- a hook beside its provider, like useAuth
export function useOptionalAuth() {
  return useContext(AuthContext);
}

/** Whether the person is a super admin; false outside an AuthProvider (a chat composer
 *  rendered on its own, as in tests), where a pre-launch feature simply stays hidden. */
export function useIsSuperAdmin(): boolean {
  return useContext(AuthContext)?.isSuperAdmin ?? false;
}
